import { useCallback, useEffect, useRef, useState } from "react";
import { DemoZustandProvider, useDemo } from "./zustand/DemoZustand";
import { AnmeldungProvider, useAnmeldung } from "./zustand/Anmeldung";
import { ChatProvider } from "./zustand/ChatZustand";
import Wiederherstellungsschluessel from "./bausteine/Wiederherstellungsschluessel";
import Geraetefreischaltung, { Bestaetigungsanfrage } from "./bausteine/Geraetefreischaltung";
import Anmelden from "./ansichten/Anmelden";
import Dashboard from "./ansichten/Dashboard";
import Sitzungsraum from "./ansichten/Sitzungsraum";
import ChatsAnsicht from "./ansichten/Chats";
import KanalAnsicht from "./ansichten/Kanal";
import AufgabenAnsicht from "./ansichten/Aufgaben";
import DateienAnsicht from "./ansichten/Dateien";
import Office from "./ansichten/Office";
import Praesentation from "./ansichten/Praesentation";
import Tabelle from "./ansichten/Tabelle";
import Textdokument from "./ansichten/Textdokument";
import Profil from "./ansichten/Profil";
import StandbildAnsicht from "./ansichten/Standbilder";
import Menue, { aktiverMenuepunkt } from "./bausteine/Menue";
import Tagesordnungsspalte from "./bausteine/Tagesordnungsspalte";
import DemoEinstellungen from "./bausteine/DemoEinstellungen";
import { useMedienabfrage } from "./hilfen/medien";
import { kurzesDatum, uhrzeit, wochentag } from "./hilfen/datum";

/*
  Rahmen der Anwendung.

  Zuerst die Frage: Ist jemand angemeldet? Ohne Anmeldung gibt es nur das Tor.
  Es gibt keinen Weg daran vorbei — auch keine Demo-Tür. Das ist eine bewusste
  Entscheidung; sie bedeutet, dass ein Vorführtermin Netz braucht.

  Angemeldet dann drei Zonen:
    Menü           links, immer sichtbar (schmal: Leiste oben)
    Tagesordnung   die mittlere Spalte, solange man an der Sitzung arbeitet
    Inhaltsspalte  die eigentliche Ansicht

  Die Tagesordnungsspalte wechselt ihren Inhalt nicht mehr. Sie steht still,
  während rechts gearbeitet wird — das war der Grund, warum man sich beim
  Vorbereiten verlaufen konnte.
*/

const BASIS = { name: "dashboard", parameter: {} };

/*
  1024 Pixel, nicht mehr 1120. Ein iPad 10,2" im Querformat hat 1080 Pixel —
  mit der alten Schwelle blieb die zweite Spalte ausgerechnet auf dem Gerät
  aus, für das die Anwendung gebaut ist.
*/
const ZWEI_SPALTEN = "(min-width: 64rem)";

export default function App() {
  return (
    <AnmeldungProvider>
      <Tor />
    </AnmeldungProvider>
  );
}

function Tor() {
  const { angemeldet, wirdGeprueft } = useAnmeldung();

  if (wirdGeprueft) {
    return (
      <div className="warten">
        <p>Einen Moment — die Anmeldung wird geprüft.</p>
      </div>
    );
  }

  if (!angemeldet) return <Anmelden />;

  return (
    <DemoZustandProvider>
      <MitChat />
    </DemoZustandProvider>
  );
}

/*
  Der Chat hängt an den Kanälen, und die kennt erst der Zustand der App —
  deshalb dieser Zwischenschritt. Er steht innerhalb von DemoZustandProvider
  und außerhalb von allem, was Beiträge anzeigt.
*/
function MitChat() {
  const { stammdaten } = useDemo();
  return (
    <ChatProvider kanaele={stammdaten.kanaele || []}>
      <Rahmen />
      {/*
        Liegt über allem: Wer den Schlüssel wegklickt, ohne ihn zu notieren,
        bekommt ihn nie wieder. Deshalb darf er nicht in einer Ecke stehen.
      */}
      <Wiederherstellungsschluessel />

      {/*
        Ein neues Gerät, das freigeschaltet werden will — und die Anfrage,
        die auf einem laufenden Gerät ankommt. Beide liegen über allem:
        Wer bestätigen soll, muss die Frage sehen.
      */}
      <Geraetefreischaltung />
      <Bestaetigungsanfrage />
    </ChatProvider>
  );
}

function Rahmen() {
  const { stammdaten } = useDemo();
  const { konto, fraktion, bildAdresse } = useAnmeldung();
  const zweiSpalten = useMedienabfrage(ZWEI_SPALTEN);

  const [verlauf, setzeVerlauf] = useState([BASIS]);
  const [einstellungenOffen, setzeEinstellungenOffen] = useState(false);
  const detailRef = useRef(null);

  const nachObenScrollen = useCallback(() => {
    if (detailRef.current) detailRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
  }, []);

  const zeige = useCallback(
    (name, parameter = {}) => {
      setzeVerlauf((bisher) => [...bisher, { name, parameter }]);
      nachObenScrollen();
    },
    [nachObenScrollen]
  );

  const oeffne = useCallback(
    (name, parameter = {}) => {
      setzeVerlauf(name === BASIS.name ? [BASIS] : [BASIS, { name, parameter }]);
      nachObenScrollen();
    },
    [nachObenScrollen]
  );

  const zurueck = useCallback(() => {
    setzeVerlauf((bisher) => (bisher.length > 1 ? bisher.slice(0, -1) : bisher));
    nachObenScrollen();
  }, [nachObenScrollen]);

  const zumDashboard = useCallback(() => {
    setzeVerlauf([BASIS]);
    nachObenScrollen();
  }, [nachObenScrollen]);

  const aktuell = verlauf[verlauf.length - 1];

  /* Nur im Sitzungsraum steht die Tagesordnung daneben. */
  const mitKontext = zweiSpalten && aktuell.name === "sitzung";
  const weit = aktuell.name !== "sitzung" || !aktuell.parameter.topId;

  return (
    <>
      <Kopfleiste
        fraktion={fraktion}
        koerperschaft={stammdaten.koerperschaft}
        konto={konto}
        bildAdresse={bildAdresse}
        zeigeZurueck={verlauf.length > 1}
        aufZurueck={zurueck}
        aufMarke={zumDashboard}
        aufProfil={() => oeffne("profil", {})}
      />

      {einstellungenOffen && (
        <DemoEinstellungen
          aufSchliessen={() => setzeEinstellungenOffen(false)}
          aufZurueckgesetzt={zumDashboard}
        />
      )}

      <div className={`rahmen ${mitKontext ? "rahmen--fokus" : ""}`}>
        <Menue
          aktiv={aktiverMenuepunkt(aktuell.name)}
          oeffne={oeffne}
          aufEinstellungen={() => setzeEinstellungenOffen(true)}
        />

        <div className={`arbeitsflaeche ${mitKontext ? "arbeitsflaeche--geteilt" : ""}`}>
          {mitKontext && (
            <aside className="seitenleiste">
              <Tagesordnungsspalte topId={aktuell.parameter.topId} oeffne={oeffne} />
            </aside>
          )}

          <main className="detailspalte" ref={detailRef}>
            <div className={`detailspalte__inhalt ${weit ? "detailspalte__inhalt--weit" : ""}`}>
              <Ansicht ansicht={aktuell} mitKontext={mitKontext} zeige={zeige} oeffne={oeffne} />

              <footer className="fusszeile">
                <p>
                  Sitzung, Tagesordnung und Vorlagen kommen aus dem Ratsinformationssystem —
                  hier aus einem nachgestellten für {stammdaten.koerperschaft.name}, eine
                  erfundene Kommune. Meinungsbilder, Markierungen und Aufgaben sind
                  Beispieldaten. Echt sind Konten und Dateien.
                </p>
                <p>
                  Vorbereitend, nicht rechtsverbindlich. Kein Ersatz für das
                  Ratsinformationssystem.
                </p>
              </footer>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

/* ---------- Kopfzeile ---------- */

function Kopfleiste({
  fraktion,
  koerperschaft,
  konto,
  bildAdresse,
  zeigeZurueck,
  aufZurueck,
  aufMarke,
  aufProfil,
}) {
  const [jetzt, setzeJetzt] = useState(() => new Date());

  useEffect(() => {
    const takt = window.setInterval(() => setzeJetzt(new Date()), 30000);
    return () => window.clearInterval(takt);
  }, []);

  const zeile = fraktion
    ? `${fraktion.name} · ${fraktion.kommune}`
    : `${koerperschaft.name}`;

  return (
    <header className="kopfleiste">
      <div className="kopfleiste__inhalt">
        {zeigeZurueck ? (
          <button className="kopfleiste__zurueck" onClick={aufZurueck} aria-label="Zurück">
            <span aria-hidden="true">←</span>
            <span className="kopfleiste__zurueck-text">Zurück</span>
          </button>
        ) : null}

        <button
          className="kopfleiste__marke"
          onClick={aufMarke}
          style={{ background: "none", border: 0, padding: 0 }}
        >
          Fraktionsbüro
        </button>

        <span className="kopfleiste__fraktion">{zeile}</span>

        <span className="kopfleiste__chip kopfleiste__uhr">
          {wochentag(jetzt).slice(0, 2)}., {kurzesDatum(jetzt)} · {uhrzeit(jetzt)}
        </span>

        <button className="kopfleiste__ich" onClick={aufProfil} title={konto.name}>
          {bildAdresse ? (
            <img src={bildAdresse} alt="" />
          ) : (
            <span aria-hidden="true">{anfangsbuchstaben(konto.name)}</span>
          )}
          <span className="kopfleiste__ich-name">{ersterName(konto.name)}</span>
        </button>
      </div>
    </header>
  );
}

function anfangsbuchstaben(name) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((t) => t[0])
    .join("")
    .toUpperCase();
}

function ersterName(name) {
  return name ? name.split(/\s+/)[0] : "Profil";
}

/* ---------- Welche Ansicht? ---------- */

function Ansicht({ ansicht, mitKontext, zeige, oeffne }) {
  const { name, parameter } = ansicht;

  switch (name) {
    case "dashboard":
      return <Dashboard oeffne={oeffne} />;

    case "sitzung":
      return (
        <Sitzungsraum
          topId={parameter.topId}
          reiter={parameter.reiter}
          zitat={parameter.zitat}
          mitKontext={mitKontext}
          oeffne={oeffne}
        />
      );

    case "chats":
      return <ChatsAnsicht oeffne={oeffne} />;

    case "kanal":
      return <KanalAnsicht kanalId={parameter.kanalId} zitat={parameter.zitat} oeffne={oeffne} />;

    case "aufgaben":
      /*
        Ein Verweis aus dem Kanal führt nicht nur ins Brett, sondern auf die
        gemeinte Karte — sonst müsste man sie zwischen den Spalten suchen.
      */
      return <AufgabenAnsicht oeffne={oeffne} aufgabeId={parameter.aufgabeId} />;

    case "office":
      return <Office oeffne={oeffne} />;

    case "dateien":
      return <DateienAnsicht />;

    case "textdokument":
      return <Textdokument dokumentId={parameter.dokumentId} oeffne={oeffne} />;

    case "praesentation":
      return <Praesentation dokumentId={parameter.dokumentId} oeffne={oeffne} />;

    case "tabelle":
      return <Tabelle dokumentId={parameter.dokumentId} oeffne={oeffne} />;

    case "profil":
      return <Profil oeffne={oeffne} />;

    case "standbild":
      return <StandbildAnsicht welches={parameter.welches} />;

    default:
      return (
        <div className="karte" style={{ marginTop: "1.5rem" }}>
          <p>Diese Ansicht wurde nicht gefunden.</p>
          <button
            className="knopf knopf--zurueckhaltend"
            style={{ marginTop: "var(--luft-4)" }}
            onClick={() => oeffne("dashboard", {})}
          >
            Zum Dashboard
          </button>
        </div>
      );
  }
}
